import { Controller } from "@hotwired/stimulus"

// Inline per-field autosave for the user settings page.
//
// Behaviour (mirrors docs handoff "B · Inset"):
//   • Safe fields (name, racing identity, timezone) save themselves on change,
//     debounced, with an inline idle → saving → saved/error indicator next to
//     their label.
//   • Toggles (notifications) save instantly; the persisted value can be the
//     inverse of the visible state (opt-out columns).
//   • The profile photo saves on file pick; "remove" purges it. Both reload the
//     page so the rendered avatar reflects the new state.
//   • The email and password GROUPS are not free-autosave: editing them reveals
//     a Save action (and, for password accounts, a current-password gate). The
//     whole group PATCHes together so Devise can validate the gate.
//
// Every save is a `PATCH user_registration_path` with `Accept: application/json`;
// the controller renders `{ ok, errors, reload, notice }`. Window events
// (`settings:save-begin` / `settings:save-end`) drive the header SavePill.
//
// HTML contract (data attributes, all scoped to this controller's <form>):
//   field input      data-action="input->settings-autosave#schedule" (or change->)
//   toggle button    data-action="settings-autosave#toggle" data-invert="true|false"
//                    + aria-checked + data-field
//   photo file input data-action="change->settings-autosave#photo"
//   remove photo     data-action="settings-autosave#removePhoto"
//   group container  data-group="email|password"
//     dirty trigger    data-action="input->settings-autosave#dirty"
//     gate row         data-role="gate"     (hidden until dirty)
//     actions row      data-role="actions"  (hidden until dirty; holds Save + status)
//     save button      data-action="settings-autosave#saveGroup"
//   per-field status  data-role="status" data-field="user[...]"
//   per-field error    data-role="error"  data-field="user[...]"
export default class extends Controller {
  static values = {
    url: String,
    debounce: { type: Number, default: 600 },
  }

  connect() {
    this.timers = new Map()
  }

  // With JS active every change autosaves, so a native form submit (Enter key,
  // or the no-JS fallback button) must not fire — it would bypass the email /
  // password current-password gate. No-JS clients lack this controller and
  // submit normally.
  blockSubmit(event) {
    event.preventDefault()
  }

  disconnect() {
    this.timers.forEach((t) => clearTimeout(t))
    this.timers.clear()
  }

  // ── Single-field autosave ────────────────────────────────────────────────

  schedule(event) {
    const input = event.target
    const name = input.name
    if (!name) return
    this.setStatus(name, "saving")
    clearTimeout(this.timers.get(name))
    this.timers.set(
      name,
      setTimeout(() => this.saveField(input), this.debounceValue),
    )
  }

  saveField(input) {
    const body = new FormData()
    body.append(input.name, input.value)
    this.patch(body)
      .then((data) => {
        if (data.ok) {
          this.clearError(input.name)
          this.setStatus(input.name, "saved")
        } else {
          this.applyErrors(data.errors)
          this.setStatus(input.name, "error", input)
        }
      })
      .catch(() => this.setStatus(input.name, "error", input))
  }

  // ── Toggles (instant save, optionally inverted) ──────────────────────────

  toggle(event) {
    const btn = event.currentTarget
    const name = btn.dataset.field
    const nowOn = btn.getAttribute("aria-checked") !== "true"
    btn.setAttribute("aria-checked", String(nowOn))
    this.renderToggle(btn, nowOn)

    const invert = btn.dataset.invert === "true"
    const persisted = invert ? !nowOn : nowOn
    this.setStatus(name, "saving")

    const body = new FormData()
    body.append(name, persisted ? "1" : "0")
    this.patch(body)
      .then((data) => this.setStatus(name, data.ok ? "saved" : "error"))
      .catch(() => this.setStatus(name, "error"))
  }

  renderToggle(btn, on) {
    btn.classList.toggle("bg-vivid-purple", on)
    btn.classList.toggle("bg-app-surface-active", !on)
    const knob = btn.querySelector("[data-knob]")
    // The travel distance rides on the button because the nested sub-toggles
    // are a smaller switch; hard-coding it here left their knob overshooting.
    if (knob)
      knob.classList.toggle(btn.dataset.travel || "translate-x-[16px]", on)
  }

  // ── Profile photo ─────────────────────────────────────────────────────────

  pickPhoto(event) {
    event.preventDefault()
    this.element
      .querySelector("input[type=file][name='user[profile_image]']")
      ?.click()
  }

  photo(event) {
    const input = event.target
    if (!input.files || input.files.length === 0) return
    const body = new FormData()
    body.append("user[profile_image]", input.files[0])
    this.savePhoto(body)
  }

  removePhoto(event) {
    event.preventDefault()
    const body = new FormData()
    body.append("user[remove_profile_image]", "1")
    this.savePhoto(body)
  }

  savePhoto(body) {
    window.dispatchEvent(new CustomEvent("settings:save-begin"))
    this.patch(body, false)
      .then((data) => {
        window.dispatchEvent(new CustomEvent("settings:save-end"))
        if (data.ok && data.reload) {
          window.Turbo
            ? Turbo.visit(window.location.href, { action: "replace" })
            : window.location.reload()
        } else if (!data.ok) {
          this.applyErrors(data.errors)
        }
      })
      .catch(() => window.dispatchEvent(new CustomEvent("settings:save-end")))
  }

  // ── Email / password groups ───────────────────────────────────────────────

  dirty(event) {
    const group = event.target.closest("[data-group]")
    if (!group) return
    group
      .querySelectorAll("[data-role=gate], [data-role=actions]")
      .forEach((el) => {
        el.style.display = ""
      })
  }

  saveGroup(event) {
    event.preventDefault()
    const group = event.currentTarget.closest("[data-group]")
    if (!group) return
    const inputs = Array.from(group.querySelectorAll("input"))
    const body = new FormData()
    inputs.forEach((i) => body.append(i.name, i.value))

    const statusName = group.dataset.group
    this.setStatus(statusName, "saving")
    inputs.forEach((i) => this.clearError(i.name))

    this.patch(body)
      .then((data) => {
        if (data.ok) {
          this.setStatus(statusName, "saved")
          group
            .querySelectorAll("[data-role=gate], [data-role=actions]")
            .forEach((el) => {
              el.style.display = "none"
            })
          inputs.forEach((i) => {
            if (i.type === "password") i.value = ""
          })
          if (data.notice) this.showNotice(group, data.notice)
        } else {
          this.applyErrors(data.errors)
          this.setStatus(statusName, "error")
        }
      })
      .catch(() => this.setStatus(statusName, "error"))
  }

  showNotice(group, text) {
    const node = group.querySelector("[data-role=notice]")
    if (!node) return
    node.textContent = text
    node.style.display = ""
  }

  // ── Network ────────────────────────────────────────────────────────────────

  patch(body, countForPill = true) {
    if (countForPill)
      window.dispatchEvent(new CustomEvent("settings:save-begin"))
    return fetch(this.urlValue, {
      method: "PATCH",
      body,
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
    })
      .then((response) => response.json().catch(() => ({ ok: response.ok })))
      .finally(() => {
        if (countForPill)
          window.dispatchEvent(new CustomEvent("settings:save-end"))
      })
  }

  csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  // ── Inline status + error rendering ─────────────────────────────────────────

  setStatus(field, state, input) {
    const node = this.statusNode(field)
    if (node) node.innerHTML = STATUS_MARKUP[state] || ""
    if (state === "saved") {
      setTimeout(() => {
        if (node && node.innerHTML === STATUS_MARKUP.saved) node.innerHTML = ""
      }, 1800)
    }
    if (state === "error" && input)
      input.classList.add("!border-red-500", "focus:!ring-red-500/20")
  }

  statusNode(field) {
    return this.element.querySelector(
      `[data-role=status][data-field="${cssEscape(field)}"]`,
    )
  }

  applyErrors(errors) {
    if (!errors) return
    Object.entries(errors).forEach(([attr, message]) => {
      const field = `user[${attr}]`
      const errorNode = this.element.querySelector(
        `[data-role=error][data-field="${cssEscape(field)}"]`,
      )
      const input = this.element.querySelector(`[name="${cssEscape(field)}"]`)
      if (errorNode) {
        const text =
          errorNode.querySelector("[data-role=error-text]") || errorNode
        text.textContent = capitalize(message)
        errorNode.style.display = ""
      }
      if (input)
        input.classList.add("!border-red-500", "focus:!ring-red-500/20")
    })
  }

  clearError(field) {
    const errorNode = this.element.querySelector(
      `[data-role=error][data-field="${cssEscape(field)}"]`,
    )
    const input = this.element.querySelector(`[name="${cssEscape(field)}"]`)
    if (errorNode) errorNode.style.display = "none"
    if (input)
      input.classList.remove("!border-red-500", "focus:!ring-red-500/20")
  }
}

const SPINNER = `<svg viewBox="0 0 24 24" fill="none" class="w-3.5 h-3.5 animate-spin text-app-text-faint"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.25" stroke-width="3"/><path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`
const CHECK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-vivid-purple"><path d="M5 13l4 4L19 7"/></svg>`
const RETRY = `<span class="inline-flex items-center gap-1 text-[11px] font-medium text-red-500"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3"><path d="M12 9v4m0 4h.01M10.3 3.9 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>Retry</span>`

const STATUS_MARKUP = { idle: "", saving: SPINNER, saved: CHECK, error: RETRY }

function cssEscape(value) {
  return value.replace(/"/g, '\\"')
}

function capitalize(text) {
  if (!text) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}
